import { useEffect, useRef, useState } from 'react';
import { Trash } from 'lucide-react';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Modal } from '../ui/Modal.js';
import { ConfirmDialog } from '../ui/ConfirmDialog.js';
import { useHabits } from '../stores/habits.js';
import { useUi } from '../stores/ui.js';
import { Field } from '../ui/Field.js';
import { ColorPicker } from '../ui/ColorPicker.js';
import { HABIT_PALETTE, affichee } from '../lib/palette.js';

const PALETTE = HABIT_PALETTE;

const EMOJI_SUGGESTIONS = ['💪', '🏃', '📚', '🧘', '💧', '🥗', '😴', '✍️', '🎯', '🧠', '🎨', '🎸'];

export function HabitFormModal(): React.ReactElement | null {
  const open = useUi((s) => s.habitFormOpen);
  const editingId = useUi((s) => s.habitFormId);
  const close = useUi((s) => s.closeHabitForm);
  const habits = useHabits((s) => s.habits);
  const { create, update, remove } = useHabits();

  const existing = editingId ? habits.find((h) => h.habitId === editingId) : null;

  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('');
  const [color, setColor] = useState<string>(PALETTE[0]!);
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name);
      setEmoji(existing.emoji ?? '');
      setColor(affichee(existing.color) ?? PALETTE[0]!);
    } else {
      setName('');
      setEmoji('');
      setColor(PALETTE[0]!);
    }
    setSaving(false);
  }, [open, existing]);

  if (!open) return null;

  async function save(): Promise<void> {
    const trimmed = name.trim();
    if (!trimmed || saving) return;
    setSaving(true);
    try {
      if (existing) {
        await update(existing.habitId, {
          name: trimmed,
          emoji: emoji || null,
          color,
        });
      } else {
        const input: { name: string; emoji?: string; color?: string } = {
          name: trimmed,
          color,
        };
        if (emoji) input.emoji = emoji;
        await create(input);
      }
      close();
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete(): Promise<void> {
    if (!existing) return;
    setConfirmingDelete(false);
    await remove(existing.habitId);
    close();
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={existing ? 'Edit habit' : 'New habit'}
      initialFocusRef={nameRef}
      footer={
        <>
          {existing && (
            <Button variant="danger" className="mr-auto" onClick={() => setConfirmingDelete(true)}>
              <Trash size={16} aria-hidden />
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={close}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!name.trim()} loading={saving}>
            {existing ? 'Save' : 'Create'}
          </Button>
        </>
      }
    >
      <div className="pile">
        <Field label="Name">
          <Input
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                save();
              }
            }}
            placeholder="Sport, Méditation, Lire 30 min…"
            maxLength={80}
          />
        </Field>

        <Field label="Emoji (optional)" group>
          <div className="flex items-start gap-2">
            <Input
              value={emoji}
              onChange={(e) => setEmoji(e.target.value)}
              placeholder="💪"
              aria-label="Emoji"
              className="w-16 shrink-0 text-center px-1"
              maxLength={8}
            />
            <div className="flex flex-wrap">
              {EMOJI_SUGGESTIONS.map((e) => (
                <Button
                  key={e}
                  variant="quiet"
                  size="icon-small"
                  aria-pressed={emoji === e}
                  onClick={() => setEmoji(e)}
                >
                  {e}
                </Button>
              ))}
            </div>
          </div>
        </Field>

        <Field label="Color" group>
          <ColorPicker palette={HABIT_PALETTE} value={color} onChange={setColor} label="Color" />
        </Field>
      </div>

      <ConfirmDialog
        open={confirmingDelete}
        title={existing ? `Delete "${existing.name}"?` : 'Delete habit?'}
        message="The habit and all its history will be deleted."
        confirmLabel="Delete"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirmingDelete(false)}
      />
    </Modal>
  );
}
